// The auth package's PostgREST RPC calls (my_memberships, team_roster, leave_company) and Edge Function calls
// (delete-account), with WEB_PLAN.md §4.6's session rule: an HTTP 401 gets one `refreshSession()` and a retry; if
// the refresh is refused, or the retry is refused again, the browser signs out ("Please sign in again"). A refresh
// that got no answer is a connection problem (NetworkError), never a sign-out. Errors come out as the classes in
// ./errors so the screens word them like iOS.

import { supabase } from '@/supabase';
import { isNetworkError, NetworkError, NOT_DEPLOYED_CODE, ServerError, SessionExpiredError } from './errors';
import { peekSignedOutNotice, setSignedOutNotice } from './pending';

interface PostgrestLikeError {
  message?: string;
  code?: string;
  details?: string | null;
  hint?: string | null;
}

/**
 * Refreshes the session once after a 401. Like iOS `performRefresh`, only a refused refresh (token gone, revoked or
 * already used) means "sign in again"; a refresh that got no answer (offline, or GoTrue's 5xx, which supabase-js
 * reports as a retryable fetch error and keeps the session for) is a connection problem and throws NetworkError, so
 * a flaky network never signs anyone out.
 */
async function refreshOnce(): Promise<'refreshed' | 'refused'> {
  let result: Awaited<ReturnType<typeof supabase.auth.refreshSession>>;
  try {
    result = await supabase.auth.refreshSession();
  } catch (e) {
    if (isNetworkError(e)) throw new NetworkError((e as Error).message);
    return 'refused';
  }
  const { data, error } = result;
  if (!error && data.session) return 'refreshed';
  if (error && (error.name === 'AuthRetryableFetchError' || isNetworkError(error))) {
    throw new NetworkError(error.message);
  }
  return 'refused';
}

/**
 * The session can't be recovered: sign this browser out and tell the sign-in screen why, unless it already has
 * something to say (right after "Delete my account", a sync call refused for the deleted user must not turn
 * "Account deleted" into "Your session expired").
 */
async function sessionExpired(): Promise<never> {
  if (!peekSignedOutNotice()) {
    setSignedOutNotice({ title: 'Signed out', message: 'Your session expired. Please sign in again.' });
  }
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch {
    // The local session is removed either way; SIGNED_OUT wipes local data.
  }
  throw new SessionExpiredError();
}

/**
 * Calls `public.<name>(params)` and returns its JSON. RPC errors raise `ServerError` (status, SQLSTATE code, and the
 * message, which for permission errors is exactly a `ServerErrorKind` such as "last_admin"); no answer raises
 * `NetworkError`.
 */
export async function callRpc<T>(name: string, params?: Record<string, unknown>): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    const { data, error, status } = await supabase.rpc(name, params ?? {});
    if (!error) return data as T;
    const e = error as PostgrestLikeError;
    if (status === 0) throw new NetworkError(e.message ?? 'Network request failed');
    if (status === 401) {
      if (attempt === 0 && (await refreshOnce()) === 'refreshed') continue;
      return sessionExpired();
    }
    throw new ServerError(status, e.code ?? null, e.message ?? '');
  }
}

/** What an Edge Function answered: HTTP status and the parsed JSON body (null when not JSON). */
export interface FunctionAnswer {
  status: number;
  body: Record<string, unknown> | null;
}

async function readJson(response: Response): Promise<Record<string, unknown> | null> {
  try {
    const parsed: unknown = await response.clone().json();
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * POST /functions/v1/<name> with the user's JWT. Returns every HTTP answer (2xx or not) so callers can read
 * function-specific refusals (delete-account's 409); 401 gets the refresh-and-retry. A request that never got an
 * answer (offline, or the browser blocked it for CORS) raises `NetworkError`.
 */
export async function invokeFunction(name: string, body: Record<string, unknown>): Promise<FunctionAnswer> {
  for (let attempt = 0; ; attempt += 1) {
    const { data, error, response } = await supabase.functions.invoke(name, { body });
    if (!error) {
      const json = typeof data === 'object' && data !== null && !Array.isArray(data) ? (data as Record<string, unknown>) : null;
      return { status: response?.status ?? 200, body: json };
    }
    const errName = (error as { name?: string }).name;
    if (errName === 'FunctionsFetchError') throw new NetworkError((error as Error).message);
    const context = (error as { context?: unknown }).context;
    const res = context instanceof Response ? context : response;
    if (!res) throw new NetworkError((error as Error).message);
    if (res.status === 401) {
      if (attempt === 0 && (await refreshOnce()) === 'refreshed') continue;
      return sessionExpired();
    }
    return { status: res.status, body: await readJson(res) };
  }
}

/**
 * Error replies of Edge Functions (`SupabaseError.fromFunction`): `{"error": code, "message": text}` refusals become
 * refused ServerErrors; a 404 without a code means the function isn't deployed; 401, 429 and 5xx stay plain HTTP
 * errors, read like `SupabaseError.from` (code: error_code, code, error, reason; text: message, msg,
 * error_description, error, reason).
 */
export function functionError(answer: FunctionAnswer): ServerError {
  const { status, body } = answer;
  const str = (v: unknown) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);
  if (status >= 400 && status < 500 && status !== 401 && status !== 429) {
    const code = str(body?.error);
    if (code) return new ServerError(status, code, str(body?.message) ?? code, true);
    if (status === 404) return new ServerError(status, NOT_DEPLOYED_CODE, '', true);
  }
  const code = str(body?.error_code) ?? str(body?.code) ?? str(body?.error) ?? str(body?.reason);
  const message = str(body?.message) ?? str(body?.msg) ?? str(body?.error_description) ?? str(body?.error)
    ?? str(body?.reason) ?? '';
  return new ServerError(status, code, message);
}
